/*
 * Description:
 * ---------------------
 * Every sentence the Remote Devices screen says.
 *
 * A remote device is something on one site's network that other sites reach,
 * each at an address the operator picks for that site (server/pkg/reach). The
 * device itself is never changed, which is the point: it is usually an old one
 * nobody can reconfigure. The reader runs a shop's IT, so the words are plain,
 * name which site does what, and answer "is it working" before any detail.
 *
 * ! file components/reachWords.js
 */

import { fmtDuration } from "../utils/formatters";

export const NOT_ON_SERVER = "This Weavian server does not have remote devices yet. It needs an update.";
export const READ_ONLY = "Your account can see remote devices but not change them.";

/* An address, or a range written as its first address and size. */
const parse = (s) => {
  const m = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})(?:\/(\d{1,2}))?$/.exec(String(s || "").trim());
  if (!m) return null;
  const octets = m.slice(1, 5).map(Number);
  if (octets.some((o) => o > 255)) return null;
  const bits = m[5] === undefined ? 32 : Number(m[5]);
  if (bits > 32) return null;
  return { value: octets.reduce((v, o) => v * 256 + o, 0), bits };
};

/* within reports whether address a lies inside range r, both as written. */
export function within(a, r) {
  const x = parse(a);
  const y = parse(r);
  if (!x || !y || y.bits > x.bits) return false;
  const size = 2 ** (32 - y.bits);
  return Math.floor(x.value / size) === Math.floor(y.value / size);
}

/* overlaps reports whether two addresses or ranges share any address. */
export const overlaps = (a, b) => within(a, b) || within(b, a);

const dotted = (v) => [24, 16, 8, 0].map((s) => Math.floor(v / 2 ** s) % 256).join(".");
const bitsOf = (s) => parse(s)?.bits ?? 32;

const PRIVATE = ["10.0.0.0/8", "172.16.0.0/12", "192.168.0.0/16", "100.64.0.0/10"];
const RESOLVERS = ["1.1.1.1", "1.0.0.1", "8.8.8.8", "8.8.4.4", "9.9.9.9", "149.112.112.112"];

/* The LAN a site reports, if its software does. */
export const lanOf = (node) => node?.payload?.lan_prefix || "";

/* Why a ticked site reaches a device at the address it does, said under it: a
   note, or a warning when the choice takes something away from that site.
   holder is the device's own site, rows what its box sees, for examples, and
   fleet the Weavian address, once there is one. */
export function addressWhy(target, holder, siteId, node, address, rows = [], fleet = "") {
  const a = String(address || "").trim();
  const lan = lanOf(node);
  const note = (text) => ({ tone: "note", text });
  const warn = (text) => ({ tone: "warn", text });
  const range = isRange(target);
  if (!a) {
    const why = lan && overlaps(target, lan) ? `${siteId} uses the same addresses (${lan}) itself, so Weavian picks ${range ? "a range" : "one"} that is free there.` : "";
    const ex = range && fleet ? examples(target, fleet, rows) : "";
    const later = fleet ? "" : `You see ${range ? "the range" : "it"} once you share.`;
    return note([why, range && "Each device keeps its last number.", ex && `For example, ${ex}.`, later].filter(Boolean).join(" "));
  }
  if (!parse(a)) return note("");
  if (RESOLVERS.some((r) => within(a, r) || within(r, a))) {
    return warn(`${a} is a public DNS server that many devices, and our own boxes, use. ${siteId} would lose it. Pick another address.`);
  }
  if (range) {
    if (lan && overlaps(a, lan)) return warn(`${siteId} uses ${lan} itself. Leave it empty for Weavian to pick, or use a range nothing there uses.`);
    const how = a === target
      ? `${siteId}'s own network${lan ? ` (${lan})` : ""} does not use them.`
      : "Each device keeps its last number.";
    const ex = examples(target, a, rows);
    return note(ex ? `${how} For example, ${ex}.` : how);
  }
  if (lan && within(a, lan)) {
    return note(`Inside ${siteId}'s own network (${lan}), so even a device there with no gateway finds it. Nothing at ${siteId} may already use this address.`);
  }
  if (!PRIVATE.some((p) => within(a, p))) {
    return warn(`A public internet address: ${siteId} reaches the device at it instead of whatever uses it on the internet.`);
  }
  if (a === target && lan) return note(`The same address it has at ${holder}: ${siteId}'s own network (${lan}) does not use it.`);
  return note("");
}

/* Two devices of a shared network as a site reaches them, for the sentence
   under it: "till is 10.9.0.12 and printer is 10.9.0.20". */
function examples(target, at, rows) {
  const picked = rows.filter((r) => r.ip && r.name !== r.ip && within(r.ip, target)).slice(0, 2);
  const words = picked.map((r) => `${r.name} is ${dialedAddress({ lan_address: target }, { address: at }, r.ip)}`);
  return words.join(" and ");
}

/* The site's LAN, said under the device's address field. */
export function lanHint(node) {
  const lan = lanOf(node);
  return lan ? `This box's network is ${lan}.` : "This box's software does not report its network yet; the box checks the address itself.";
}

/* How one site stands. */
const STATE = {
  in_force: "working",
  pending: "picking up the change",
  failed: "could not apply it",
  offline: "offline",
  old_build: "needs a software update first",
  not_registered: "no longer registered with this server",
  no_tunnel: "runs without the Weavian tunnel, so it cannot take part",
  other_domain: "now belongs to another customer",
  other_anchor: "is on another Weavian cloud anchor than the device's site",
};
export const stateWords = (s) => STATE[s.state] || s.state;

/* The measurements behind a device, for the disclosure. */
export function detailLines(device) {
  const out = [
    `Weavian address: ${device.fleet_address}. The Weavian network carries it by this address, and a site left to Weavian reaches it there.`,
    device.keep_source
      ? "The device sees each calling site's own tunnel address, so it must have its site's box as its gateway."
      : "The device sees every caller as its own site's box, so it answers even with no gateway set.",
  ];
  for (const s of device.sites) out.push(`${s.site}: ${stateWords(s)}${s.error ? `: ${s.error}` : ""}.`);
  if (device.updated_by) out.push(`Last changed by ${device.updated_by} on ${new Date(device.updated_at).toLocaleString()}.`);
  return out;
}

export const KEEP_SOURCE_LABEL = "Let the device see which site is calling";
export const KEEP_SOURCE_MEANS =
  "Leave this off for old devices: the device then sees every caller as its own site's Weavian box, so it answers even "
  + "with no gateway set. Turn it on only if the device must know which site is calling; its gateway must then be its "
  + "site's Weavian box.";
export const FLEET_LABEL = "Weavian address";
export const fleetMeans = (pool) => `Weavian picks it from its own range, ${pool}, which no box's own network may use, and `
  + "every box left to Weavian reaches the device there. Set one only if a box must use an exact address from that range.";
export const ADVANCED = "Advanced";

/* The request body for a form. */
export function bodyFromForm(form) {
  return {
    name: form.name.trim(),
    site: form.site,
    lan_address: form.lan_address.trim(),
    fleet_address: form.fleet_address.trim(),
    reached_from: form.callers.filter((c) => c.site).map((c) => ({ site: c.site, address: c.address.trim() })),
    people: form.people,
    keep_source: form.keep_source,
    disabled: form.disabled,
  };
}

/* The form for a device, or for a new one. */
export function formFromDevice(device) {
  if (!device) return { name: "", site: "", lan_address: "", fleet_address: "", callers: [{ site: "", address: "" }], people: [], keep_source: false, disabled: false };
  return {
    name: device.name,
    site: device.site,
    lan_address: device.lan_address,
    fleet_address: device.fleet_address,
    callers: device.reached_from.length ? device.reached_from.map((c) => ({ site: c.site, address: c.address || "" })) : [{ site: "", address: "" }],
    people: device.people || [],
    keep_source: !!device.keep_source,
    disabled: !!device.disabled,
  };
}

/* ── Access ───────────────────────────────────────────────────────────────
   One source box at a time: the devices at other boxes that devices at the
   source may connect to, each by the address the source uses. Every access is
   a rule from one source box to one device, with its setup, the traffic its
   two boxes have observed, and, for an address inside the source's own
   network, whether the source's box answers for it. */

export const CUSTOMER_LABEL = "Customer";
export const NO_CUSTOMER = "No customer yet";
export const NO_BOXES = "No boxes yet. A box appears here once it runs with the Weavian tunnel.";
export const onlyBox = (box, customer) => `${box} is ${customer}'s only box. Sharing needs a second one.`;
export const FROM_LABEL = "Connections from";
export const FIND = "Find a device or box";
export const ALLOW_ACCESS = "Allow access";
export const SOURCE_CAPTION = "Devices at this box";
export const LOCAL_IP = "Local IP";
export const everyDevice = (lan) => `Every device on ${lan}`;
export const deviceCount = (n) => `${n} device${n === 1 ? "" : "s"}`;
export const TONE_WORDS = { wait: "Setting up", off: "Paused", bad: "Failed" };
export const SETUP_LABEL = "Setup";
export const SETUP_WORDS = { applied: "Applied", setting_up: "Setting up", paused: "Paused", failed: "Failed" };
export const TRAFFIC_LABEL = "Traffic observed";
export const ADDRESS_LABEL = "Address check";
export const ADDRESS_WORDS = { passed: "Passed", failed: "Failed" };
export const REFRESH = "Refresh";
export const MANAGE = "Manage access";
export const MORE = "More actions";
export const PAUSE_ALL = "Pause all access";
export const RESUME_ALL = "Resume all access";
export const removeAccess = (box) => `Remove ${box}'s access`;
export const removeTitle = (box, name) => `Remove ${box}'s access to ${name}?`;
export const removeMessage = (box, last, people) => `Devices at ${box} can no longer connect to it, and connections open now are ended.`
  + (last && !people ? " Nothing else connects to it, so it stops being shared." : "")
  + (last && people ? " People keep reaching it from their laptops and phones." : "");
export const connectFrom = (box) => `Connect from ${box} using`;
export const scopeLine = (box) => `Every device at ${box} may connect, on any port.`;
export const reverseLine = (owner) => `Access initiated from ${owner} requires its own permission.`;
export const noAccess = (box) => `Devices at ${box} cannot connect to anything at your other boxes yet.`;
export const NO_MATCH = "No device or box matches.";
export const pathLabel = (box, name, owner, address) => `Devices at ${box} connect to ${name} at ${owner} using ${address}`;
export const asOf = (at) => `As of ${at.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}.`;

/* How the devices of a network shared whole sit in the range a box uses. */
export const rangeNote = (lan, address) => (address === lan ? "Each device at its own address." : "Each device keeps its last number.");

const BROKEN = ["failed", "other_anchor", "old_build", "no_tunnel", "other_domain", "not_registered"];

/* Where one rule's setup stands, in a word and, unless applied, why: the
   server's state for the device's own box and for the source box, and
   whether the Weavian cloud carries it yet. */
export function setupOf(entry, box, anchors) {
  if (entry.disabled) return { state: "paused", text: "Paused: no box connects until you resume it." };
  const ends = [entry.site, box].map((site) => entry.sites?.find((s) => s.site === site)).filter(Boolean);
  const broken = ends.find((s) => BROKEN.includes(s.state));
  if (broken) {
    if (broken.state === "failed") return { state: "failed", text: `${broken.site} could not apply it: ${broken.error}` };
    if (broken.state === "other_anchor") {
      return { state: "failed", text: `${broken.site} is on another Weavian cloud anchor than ${entry.site}. Put both on the same anchor in Cloud Gateway.` };
    }
    return { state: "failed", text: `${broken.site} ${stateWords(broken)}.` };
  }
  const waiting = ends.find((s) => s.state !== "in_force");
  if (waiting) {
    return { state: "setting_up", text: waiting.state === "offline" ? `${waiting.site} is offline, and takes this when it is back.` : `Saved. Waiting for ${waiting.site} to confirm it, which takes up to a minute.` };
  }
  if (cloudBehind(entry, anchors)) return { state: "setting_up", text: "Saved. Waiting for the Weavian cloud to confirm it, which takes up to a minute." };
  return { state: "applied", text: "" };
}

/* What the two boxes have observed of one rule: the connections the source
   box counted its network opening to the device, and those the device's own
   box counted arriving. mine and theirs are the two boxes' reads of /reach,
   {body, at}. A box counts again from nothing whenever it takes a change, so
   a count is an observation, never a test. */
export function trafficOf(entry, box, mine, theirs) {
  if (!mine) return `Asking ${box}'s box…`;
  if (!mine.body) return `${box}'s box did not answer.`;
  const dial = mine.body.dial?.find((d) => d.id === entry.id);
  if (!dial) return `${box}'s box has not taken this yet.`;
  if (!dial.connections) return "No connections observed yet. Each box counts from its last change.";
  const serve = theirs?.body?.serve?.find((d) => d.id === entry.id);
  const there = serve ? `; ${entry.site}'s box counted ${serve.connections} arriving` : "";
  return `${box}'s box counted ${dial.connections} connection${dial.connections === 1 ? "" : "s"} opened${there}.`;
}

/* For an address inside the source's own network, whether the source's box
   answers for it there, as the box itself reports; nothing for any other
   address, which its devices reach through their gateway. */
export function addressCheckOf(entry, box, address, node, mine) {
  const dial = mine?.body?.dial?.find((d) => d.id === entry.id);
  if (!dial?.in_force || isRange(address) || !within(address, lanOf(node))) return null;
  return dial.answers_arp
    ? { state: "passed", text: `${box}'s box answers for ${address} on its network.` }
    : { state: "failed", text: `${box}'s box is not answering for ${address} on its network. Something else there may be using it.` };
}

/* A path's colour: red when the rule failed or its address does not answer,
   grey while it is being set up or paused, the accent otherwise. */
export function pathTone(setup, check) {
  if (setup.state === "failed" || check?.state === "failed") return "bad";
  return { setting_up: "wait", paused: "off" }[setup.state] || "good";
}

/* Every rule of the customer: one per source box of each shared device. */
export const rulesOf = (entries) => entries.flatMap((e) => e.reached_from.map((c) => ({
  key: `${e.id}>${c.site}`, entry: e, box: c.site, address: c.address || e.fleet_address,
})));

/* The shared entry for one device or range at one box, if any. */
export const entryFor = (entries, site, address) => entries.find((e) => e.site === site && e.lan_address === String(address).trim()) || null;

/* ── The master map ─────────────────────────────────────────────────────────
   Every box of the customer, each standing for its network, and one line per
   direction between two boxes for what devices at one may connect to at the
   other. Addresses appear only once a line is picked. */

export const MAP_LABEL = "Your boxes, and what devices at each may connect to";
export const FIT = "Fit map";
export const ZOOM_IN = "Zoom in";
export const ZOOM_OUT = "Zoom out";
export const BACK = "Back to master map";
export const READING = "Reading its devices…";
export const SILENT = "Its box did not answer.";
export const NONE_SEEN = "Its box sees no devices yet.";
export const UPDATE_NOTE = "Needs a software update to share.";
export const SHOW_FEWER = "Show fewer";
export const showMore = (n) => `Show ${n} more`;
export const foldLabel = (box, open) => `${open ? "Collapse" : "Expand"} ${box}`;
export const focusOn = (box) => `Focus on ${box}`;
export const allowFrom = (box) => `Allow access from ${box}`;
export const SHARED_HERE = "Shared here";
export const REMOTE_ACCESS = "Remote access";
export const ACCESS_FROM = "Access from";
export const ADDRESS_PENDING = "Address pending";
export const ADD_SITE = "Add site";
export const NO_SITE_YET = "No other site has access yet. Add one.";
export const NOTHING_SHARED = "Nothing shared";
export const REACHES_NOTHING = "Its devices connect to nothing at your other boxes yet.";
export const NO_BOX_YET = "No box yet";
export const NOT_SHARED_HERE = "Not shared";
export const panelLabel = (box) => `Devices at ${box}`;
export const closePanel = (box) => `Close ${box}`;
export const DRAG = "Drag to move";
export const LET_REACH = "Let other sites reach it";
export const letReach = (name) => `Let other sites reach ${name}`;
export const atBox = (box) => `at ${box}`;

/* What a line between two boxes carries while the owner is folded: what of
   the owner's the source's devices may connect to. */
export const pairLabel = (rules) => (rules.some((r) => isRange(r.entry.lan_address)) ? "whole network" : deviceCount(rules.length));
export const pairLine = (from, to, rules) => `${from} can connect to ${pairLabel(rules)} at ${to}`;
export const pairTitle = (from, to, rules) => `Devices at ${from} can connect to ${pairLabel(rules)} at ${to}.`;
export const ruleLine = (rule) => `${rule.box} can connect to ${rule.entry.name} at ${rule.entry.site}`;

/* A box's lines on the map: what it shares, and its remote access by how its
   setup stands, so a permission still being set up never reads as working. */
export function boxSummary(shared, reaches, anchors) {
  const count = (state) => reaches.filter((r) => setupOf(r.entry, r.box, anchors).state === state).length;
  const remote = [
    count("applied") && deviceCount(count("applied")),
    count("setting_up") && `${count("setting_up")} pending`,
    count("failed") && `${count("failed")} failed`,
    count("paused") && `${count("paused")} paused`,
  ].filter(Boolean);
  const parts = [
    shared.length && (shared.some((e) => isRange(e.lan_address)) ? "Shares its network" : `Shares ${deviceCount(shared.length)}`),
    remote.length && `Remote access: ${remote.join(", ")}`,
  ].filter(Boolean);
  return parts.length ? parts : [NOTHING_SHARED];
}

/* ── A box's own devices ─────────────────────────────────────────────────── */

export const SHARE_NETWORK_MEANS = "Every device on this box's network, including ones that join later.";
export const NOT_SEEN = "not seen by the box lately";
export const SITE_SILENT = "The box did not answer, so its devices are not shown.";
export const SITE_READING = "Reading the devices…";
export const SITE_EMPTY = "The box has not seen a device on its network yet.";
export const SITE_UPDATE =
  "This box runs software older than Remote Devices. Update it, and it can share its devices and reach others.";

// A device the box saw within the last minute is active, as on the dashboard.
const ACTIVE_SEC = 60;

/* What the box calls a device it sees: its hostname, else its address. */
export const deviceLabel = (d) => (d.hostname && d.hostname !== "*" ? d.hostname : d.ip);

/* When the box last saw a device, from its seconds since. */
export function seenWords(seconds) {
  if (seconds == null) return "";
  if (seconds <= ACTIVE_SEC) return "active now";
  // Minutes, hours, then days, weeks and months, one unit each.
  return `seen ${fmtDuration(Math.max(60, seconds), 1)} ago`;
}
export const isActive = (d) => (d?.active_seconds ?? Infinity) <= ACTIVE_SEC;

/* The address a calling site reaches one device at under an entry: for a whole
   network, the same place in the range that site dials. */
export function dialedAddress(entry, caller, ip) {
  const at = caller.address || entry.fleet_address;
  const lan = parse(entry.lan_address);
  const target = parse(at);
  const x = parse(ip);
  if (!lan || lan.bits === 32 || !target || !x) return at;
  const size = 2 ** (32 - lan.bits);
  return dotted(Math.floor(target.value / size) * size + (x.value % size));
}

/* What a calling site should dial for an address or a range at another site:
   the device's own address, wherever nothing at the caller can already hold it,
   so a device that sends its users on to its own address keeps working. Blank
   otherwise, which is the fleet address. */
export function suggestAddress(lanAddress, callerLan, view, callerSite, except = null) {
  const a = String(lanAddress || "").trim();
  if (!parse(a) || !PRIVATE.some((p) => within(a, p))) return "";
  if ((callerLan && overlaps(a, callerLan)) || (view?.pool && overlaps(a, view.pool))) return "";
  const taken = (view?.devices || []).some((d) => d !== except && !(except?.id && d.id === except.id) &&
    d.reached_from.some((c) => c.site === callerSite && overlaps(c.address || d.fleet_address, a)));
  return taken ? "" : a;
}

/* Whether a site's network can be shared whole: the table maps ranges up to
   a /24, the size of a shop's network. */
export const isShareableNetwork = (lan) => !!parse(lan) && bitsOf(lan) >= 24 && bitsOf(lan) < 32;
export const isRange = (a) => bitsOf(a) < 32;

/* Whether a site's box can take part. A box that does not report its network
   runs software older than Remote Devices. */
export const readiness = (node) => (!lanOf(node) ? "update" : node?.stale ? "offline" : "ready");
export const READINESS = {
  ready: "online",
  offline: "offline",
  update: "needs a software update",
};

/* The customers an account sees, each with its sites, named as the server
   names them. Sharing never crosses a customer. */
export function customerGroups(sites, domains) {
  const names = Object.fromEntries((domains || []).map((d) => [d.id, d.name || d.id]));
  const groups = new Map();
  for (const s of sites) {
    const id = s.domain || "";
    if (!groups.has(id)) groups.set(id, { id, name: id ? names[id] || id : NO_CUSTOMER, sites: [] });
    groups.get(id).sites.push(s);
  }
  return [...groups.values()].sort((a, b) => Number(!a.id) - Number(!b.id) || a.name.localeCompare(b.name));
}

const sharesAt = (group, entries) => entries.filter((e) => group.sites.some((s) => s.node_id === e.site)).length;

/* The customer shown: the one asked for, else the one with the most shares,
   then the most sites. */
export function pickCustomer(groups, wanted, entries) {
  return groups.find((g) => g.id === wanted)
    || [...groups].sort((a, b) => sharesAt(b, entries) - sharesAt(a, entries) || b.sites.length - a.sites.length)[0]
    || null;
}

/* Whether the Weavian cloud is still picking up an entry: by the entry's own
   count where the server gives one, else by the whole table's. */
const cloudBehind = (entry, anchors) => {
  const a = entry.anchors || anchors || {};
  return (a.current ?? 0) < (a.live ?? 0);
};

/* The rows of a site's own network: every device its box sees, named by the
   entry that shares it when there is one, and every entry for a device the box
   has not seen lately. Shared first. */
export function hereRows(entries, siteId, lan, devices, answered) {
  const mine = entries.filter((e) => e.site === siteId);
  const singles = mine.filter((e) => !isRange(e.lan_address));
  const net = mine.find((e) => isRange(e.lan_address)) || null;
  const rows = (devices || [])
    .filter((d) => d.ip && d.mac && (!lan || within(d.ip, lan)))
    .map((d) => {
      const entry = singles.find((e) => e.lan_address === d.ip) || null;
      return { key: d.ip, ip: d.ip, name: entry ? entry.name : deviceLabel(d), seen: seenWords(d.active_seconds),
        active: isActive(d), device: d, entry };
    });
  for (const e of singles) {
    if (!rows.some((r) => r.entry === e)) rows.push({ key: e.lan_address, ip: e.lan_address, name: e.name, seen: answered ? NOT_SEEN : "", entry: e });
  }
  rows.sort((a, b) => Number(!!b.entry) - Number(!!a.entry) || Number(!!b.active) - Number(!!a.active) || a.name.localeCompare(b.name));
  return { rows, net };
}

/* ── The access editor ───────────────────────────────────────────────────── */

/* The editor's title: managing a device's access, or allowing new access. */
export function sheetTitle(mode, entry, start) {
  if (mode === "edit") return `Manage access to ${entry.name}`;
  return start?.manage ? `Manage access to ${start.name}` : ALLOW_ACCESS;
}

/* The line under the title: where the device is, and that it stays as it is. */
export function sheetSubtitle(mode, { name, holder, target, manage }) {
  if (mode !== "edit" && !manage) return "Choose who connects and the device they reach. Nothing on the device changes.";
  if (isRange(target)) return `Every device on ${target} at ${holder}, including ones that join later. Nothing on them changes.`;
  return `${name} is at ${holder}, Local IP ${target}. Nothing on it changes.`;
}
export const FROM_TITLE = "From";
export const TO_TITLE = "To";
export const BOX = "Box";
export const CHOOSE_BOX = "Choose a box";
export const PICK_DEVICE = "Device";
export const CHOOSE_DEVICE = "Choose a device";
export const TYPE_ADDRESS = "Another device, by its address";
export const everyDeviceAt = (box, lan) => `Every device at ${box} (${lan})`;
export const ALREADY_SHARED = "already shared";
/* Said when the device picked is shared already: allowing adds to it. */
export function addingTo(entry) {
  const who = entry.reached_from.map((c) => c.site);
  return `${entry.name} is shared${who.length ? ` with ${who.join(", ")}` : ""} already. Saving adds the boxes picked and changes nothing else.`;
}
export const netCovers = (box) => `Everything at ${box} is shared, so its devices are reached through that. Pick it to add boxes.`;
export const NONE_TO_SHARE = "This customer has no other box yet.";
export const SELECT_ALL = "All boxes";
export const PREVIEW_TITLE = "Before you save";
export const SCOPE_NOTE = "A permission cannot be narrowed to some devices or ports.";
// Who a box ticked in the editor lets connect: every device there, never some of them.
export const whoConnects = (box, lan) => `All devices at ${box}${lan ? ` (${lan})` : ""} can connect, on any port`;
export const ALL_DEVICES_AT = "All devices at";
export function previewSentence(box, name, holder, target, address) {
  const using = address || (isRange(target) ? "a range Weavian picks" : "an address Weavian picks");
  const what = isRange(target) ? `every device on ${target} at ${holder}` : `${name || target} at ${holder}`;
  return `Devices at ${box} will be allowed to connect to ${what} using ${using}.`;
}
export const STOP_SHARING = "Stop sharing";
export const CHANGE = "Change";
export const ADDRESS_HELP = "Any address that is free at that box. Leave it empty and Weavian picks one.";

/* The address a box would use, said on its path in the preview: the address,
   or what Weavian picks while there is none, and how a network's devices sit
   in it. A box left to Weavian uses the Weavian address, fleet, once there is
   one. */
export function pathAddress(target, address, fleet = "") {
  const a = String(address || "").trim();
  const range = isRange(target);
  if (a) return { address: a, note: range ? rangeNote(target, a) : "" };
  if (fleet) return { address: fleet, note: "Picked by Weavian." };
  return { address: "", note: range ? "A range Weavian picks" : "An address Weavian picks" };
}

/* The entries a whole network takes over: devices on it already shared alone. */
export const coveredBy = (entries, siteId, range) =>
  entries.filter((e) => e.site === siteId && e.lan_address !== range && !isRange(e.lan_address) && within(e.lan_address, range));

/* The take-over, said before it happens. */
export function replaceWords(covered) {
  const names = covered.map((e) => e.name);
  const list2 = names.length <= 2 ? names.join(" and ") : `${names.slice(0, 2).join(", ")} and ${names.length - 2} more`;
  const it = covered.length === 1 ? "it" : "them";
  return `${list2} ${covered.length === 1 ? "is" : "are"} already shared on ${covered.length === 1 ? "its" : "their"} own. `
    + `Sharing everything replaces that: the boxes picked then reach ${it} like every other device there, which `
    + `may change the address they use, and connections open to ${it} now restart once.`;
}

export const allowButton = (mode, n, device = true) => {
  if (mode === "edit") return "Save";
  if (!n) return "Pick a box first";
  if (!device) return "Pick a device first";
  return n === 1 ? ALLOW_ACCESS : `Allow access for ${n} boxes`;
};

/* ── People, from their laptops and phones ─────────────────────────────── */

export const ANYONE = "anyone";
export const PEOPLE_TITLE = "From laptops and phones";
export const peopleLead = (fleet) =>
  `People signed in with your organisation's Microsoft account reach it from their laptops and phones${fleet ? ` at ${fleet}` : ""}, and nothing else here. Each connection is recorded.`;
export const ANYONE_LABEL = "Anyone signed in";
export const ADD_PERSON = "Add a role or group";
export const ADD_PERSON_HINT = "An app role your directory gives people, such as LineCrew, or a group's object ID.";
export const ADD = "Add";
export const NOT_A_PERSON = "A role is letters, digits, spaces, dots, dashes or underscores, up to 64; a group is its object ID.";
export const removePerson = (label) => `Remove ${label}`;
export const admittedNow = (nodes) => `Reaching it now: ${nodes.join(", ")}.`;
export const NO_ONE_NOW = "No one it admits is signed in now.";
export const peopleNeedUpdate = (box) => `${box} needs a software update before anyone reaches it from a laptop or phone.`;

const GROUP_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ROLE = /^[A-Za-z0-9][A-Za-z0-9 ._-]{0,63}$/;

/* What the server stores for a role or group typed in, or "" when it is neither. */
export function personFrom(text) {
  const t = text.trim();
  if (GROUP_ID.test(t)) return `group:${t.toLowerCase()}`;
  return ROLE.test(t) ? `role:${t}` : "";
}

/* A stored role or group as a person reads it. */
export function personLabel(p) {
  if (p === ANYONE) return ANYONE_LABEL;
  const [kind, value] = [p.slice(0, p.indexOf(":")), p.slice(p.indexOf(":") + 1)];
  return kind === "group" ? `Group ${value.slice(0, 8)}…` : `Role ${value}`;
}
